import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingCart } from "lucide-react";
import axios from "axios";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { getCart, setQty, removeItem, CART_EVENT } from "../../lib/cart";
import { openRazorpayCheckout } from "../../lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = "block text-xs tracking-[0.16em] uppercase text-slate-400 mb-2";
const IN = "w-full bg-white/[0.04] border border-white/15 focus:border-[#C9A84C] outline-none px-4 py-3 text-[15px] text-white placeholder:text-slate-600 transition-colors";
const GST_RATE = 0.18;

export default function Cart() {
  const [items, setItems] = useState(getCart());
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [rzpPublic, setRzpPublic] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (!checkoutOpen) return;
    axios.get(`${API}/payments/config-status`).then((r) => { setRzpPublic(!!r.data.razorpay_public); }).catch(() => {});
  }, [checkoutOpen]);

  useEffect(() => {
    const h = () => setItems(getCart());
    window.addEventListener(CART_EVENT, h);
    return () => window.removeEventListener(CART_EVENT, h);
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price_inr * i.qty, 0);
  const gst = Math.round(subtotal * GST_RATE * 100) / 100;
  const total = subtotal + gst;

  const pay = async (gateway) => {
    setErr("");
    setBusy(gateway);
    try {
      const { data } = await axios.post(`${API}/payments/cart/order`, {
        name: f.name, email: f.email, phone: f.phone, address: f.address, gateway,
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
      });
      if (data.razorpay) {
        await openRazorpayCheckout({
          API: process.env.REACT_APP_BACKEND_URL, txnid: data.txnid,
          onPaid: () => window.location.assign(`/payment/success?txnid=${data.txnid}`),
          onError: (m) => { setErr(m); setBusy(""); },
        });
        return;
      }
      if (data.redirect_url) {
        window.location.assign(data.redirect_url);
        return;
      }
      const { payment_url, form } = data;
      // Browser POST is intentional: PayU receives the hosted-checkout form directly.
      const el = document.createElement("form");
      el.method = "POST";
      el.action = payment_url;
      Object.entries(form).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden";
        i.name = k;
        i.value = String(v ?? "");
        el.appendChild(i);
      });
      document.body.appendChild(el);
      el.submit();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not start the payment. Please try again.");
      setBusy("");
    }
  };

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen flex flex-col" data-testid="cart-page">
      <SiteHeader />
      <main className="flex-1 pt-28 sm:pt-36 pb-24">
        <div className="nx-wrap max-w-3xl">
          <div className="flex items-center gap-4 mb-3">
            <span className="h-px w-10 bg-nx-gold/70" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Your Selection</span>
          </div>
          <h1 className="font-marcellus text-4xl text-white mb-10">Shopping Cart</h1>

          {items.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10" data-testid="cart-empty">
              <ShoppingCart size={34} className="text-slate-600 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-slate-400 text-sm mb-4">Your cart is empty.</p>
              <Link to="/store" className="inline-block bg-[#C9A84C] hover:bg-[#dbb95c] text-[#0A1628] text-sm font-semibold px-8 py-3.5 transition-colors" data-testid="cart-browse">Browse Products</Link>
            </div>
          ) : (
            <>
              <div className="border border-white/10 divide-y divide-white/10" data-testid="cart-items">
                {items.map((i) => (
                  <div key={i.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`cart-item-${i.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{i.name}</p>
                      <p className="text-xs text-slate-500 mt-1">Unit price: ₹{i.price_inr.toLocaleString("en-IN")}</p>
                    </div>
                    <div className="flex items-center gap-1 border border-white/15">
                      <button type="button" onClick={() => setQty(i.id, i.qty - 1)} className="p-2.5 text-slate-400 hover:text-white transition-colors" aria-label="Decrease quantity" data-testid={`cart-dec-${i.id}`}><Minus size={14} /></button>
                      <span className="w-9 text-center text-sm text-white" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                      <button type="button" onClick={() => setQty(i.id, i.qty + 1)} className="p-2.5 text-slate-400 hover:text-white transition-colors" aria-label="Increase quantity" data-testid={`cart-inc-${i.id}`}><Plus size={14} /></button>
                    </div>
                    <span className="text-[#C9A84C] font-semibold w-28 text-right" data-testid={`cart-line-${i.id}`}>₹{(i.price_inr * i.qty).toLocaleString("en-IN")}</span>
                    <button type="button" onClick={() => removeItem(i.id)} className="text-slate-500 hover:text-red-400 transition-colors p-1.5 self-start sm:self-center" aria-label="Remove item" data-testid={`cart-remove-${i.id}`}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>

              <div className="mt-6 border border-[#C9A84C]/30 bg-[#C9A84C]/[0.04] p-6" data-testid="cart-summary">
                <div className="flex justify-between py-1.5 text-sm"><span className="text-slate-400">Subtotal</span><span className="text-white" data-testid="cart-subtotal">₹{subtotal.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between py-1.5 text-sm"><span className="text-slate-400">GST (18%)</span><span className="text-white" data-testid="cart-gst">₹{gst.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between py-2.5 mt-2 border-t border-white/15"><span className="text-white font-semibold">Grand Total</span><span className="text-xl font-bold text-[#C9A84C]" data-testid="cart-total">₹{total.toLocaleString("en-IN")}</span></div>
              </div>

              {!checkoutOpen ? (
                <button type="button" onClick={() => setCheckoutOpen(true)} className="mt-6 w-full bg-[#C9A84C] hover:bg-[#dbb95c] text-[#0A1628] font-bold tracking-wide text-base px-8 py-4 transition-colors" data-testid="cart-proceed">
                  Proceed to Pay — ₹{total.toLocaleString("en-IN")}
                </button>
              ) : (
                <form onSubmit={(e) => e.preventDefault()} className="mt-8 border border-[#C9A84C]/40 p-6 sm:p-7 space-y-4" data-testid="cart-checkout-form">
                  <p className="text-[10px] tracking-[0.24em] uppercase text-nx-goldlight mb-1">Payment Details</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className={L} htmlFor="cart-name">Name</label><input id="cart-name" data-testid="cart-name" className={IN} value={f.name} onChange={set("name")} required /></div>
                    <div><label className={L} htmlFor="cart-phone">Phone</label><input id="cart-phone" data-testid="cart-phone" type="tel" className={IN} value={f.phone} onChange={set("phone")} placeholder="10-digit mobile" required /></div>
                  </div>
                  <div><label className={L} htmlFor="cart-email">Email</label><input id="cart-email" data-testid="cart-email" type="email" className={IN} value={f.email} onChange={set("email")} required /></div>
                  <div><label className={L} htmlFor="cart-address">Billing Address</label><textarea id="cart-address" data-testid="cart-address" rows={2} className={`${IN} resize-none`} value={f.address} onChange={set("address")} required /></div>
                  {err && <p className="text-[12px] text-red-400" data-testid="cart-error">{err}</p>}
                  <div className={rzpPublic ? "grid gap-3 sm:grid-cols-2" : ""}>
                    {rzpPublic && (
                      <button type="submit" disabled={!!busy} onClick={() => pay("razorpay")} className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] disabled:opacity-60 text-[#0A1628] font-bold tracking-wide text-sm px-4 py-4 transition-colors" data-testid="cart-pay-razorpay">
                      {busy === "razorpay" ? "Opening…" : "Pay with Razorpay"}
                      </button>
                    )}
                    <button type="submit" disabled={!!busy} onClick={() => pay("payu")} className="w-full bg-[#C9A84C] hover:bg-[#dbb95c] disabled:opacity-60 text-[#0A1628] font-bold tracking-wide text-sm px-4 py-4 transition-colors" data-testid="cart-pay-payu">
                      {busy === "payu" ? "Redirecting…" : `Pay with PayU`}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
